import React from "react";
import { cn } from "../../lib/cn";
import { Eyebrow } from "./Eyebrow";

/**
 * The single page-title treatment for the whole app, member and admin alike.
 *
 * Every page previously rolled its own combination of eyebrow, heading size
 * and subtitle, which is why titles never matched across screens. Use this
 * instead of hand-rolling a title block.
 *
 *   <PageHeader
 *     eyebrow="Overview"
 *     title="Community Dashboard"
 *     subtitle="A live snapshot of members and businesses."
 *     actions={<Button>Export</Button>}
 *   />
 *
 * `title` accepts a string or nodes, so an accent word still works:
 *   title={<>Discover local <em className="kbc-serif">businesses</em></>}
 */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  as: Comp = "h1",
  className,
  ...props
}) {
  return (
    <div
      className={cn(
        "mb-5 flex flex-col gap-3 border-b border-line/10 pb-4 sm:flex-row sm:items-end sm:justify-between",
        className
      )}
      {...props}
    >
      <div className="min-w-0">
        {eyebrow ? <Eyebrow className="mb-1.5">{eyebrow}</Eyebrow> : null}
        <Comp className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
          {title}
        </Comp>
        {subtitle ? (
          <p className="mt-1 max-w-prose text-sm text-text">{subtitle}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

export default PageHeader;
